import { ComboboxItem, ComboboxProvider, SelectProvider } from '@ariakit/react'
import { type FC, useState } from 'react'
import { InputHelpText } from '@/components/ui/input-help-text'
import { Label } from '@/components/ui/label'
import {
	SelectComboboxInput,
	SelectComboboxList,
	SelectComboboxPopover,
	SelectItem,
	SelectTrigger
} from '@/components/ui/select-atoms-legacy'
import { Skeleton } from '@/components/ui/skeleton'
import { debounce } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'
import { useGetCountries } from '../queries/use-get-countries'

const SelectContent = ({
	isPending,
	isError,
	filteredData
}: {
	isPending: boolean
	isError: boolean
	filteredData: { code: string; name: string }[]
}) => {
	const { t } = useTranslation()

	if (isPending) {
		return Array.from({ length: 6 }, (_, i) => `skeleton-${i}`).map(
			(key) => <Skeleton height={32} key={key} />
		)
	}
	if (isError) {
		return <InputHelpText error={t('errors:default')} />
	}
	if (filteredData.length >= 1) {
		return filteredData.map((item) => (
			<SelectItem
				key={item.code}
				render={<ComboboxItem />}
				value={item.code}
			>
				{item.name}
			</SelectItem>
		))
	}
	return <InputHelpText helpText={t('common:no_results')} />
}

interface CountriesSelectProps {
	errorMessage?: string
	onChange: (value: string) => void
	value: string
}
export const CountriesSelect: FC<CountriesSelectProps> = ({
	errorMessage,
	onChange,
	value
}) => {
	const { t } = useTranslation()
	const countriesQuery = useGetCountries()
	const [search, setSearch] = useState('')

	const onSearch = debounce(setSearch, 300)
	const filteredData =
		countriesQuery.data?.data.filter((country) =>
			country.name.toLowerCase().includes(search.toLowerCase())
		) ?? []

	return (
		<ComboboxProvider setValue={onSearch}>
			<SelectProvider defaultValue='' setValue={onChange} value={value}>
				<div className='flex w-full flex-col gap-1.5'>
					<Label>{t('experience_shell:country_region')}</Label>
					<SelectTrigger
						className='w-full'
						disabled={countriesQuery.isPending}
						displayValue={(value) =>
							countriesQuery.data?.data.find(
								(country) => country.code === value
							)?.name
						}
						placeholder={t(
							'experience_shell:select_country_region'
						)}
					/>
					<InputHelpText error={errorMessage} />
				</div>
				<SelectComboboxPopover>
					<SelectComboboxInput autoFocus />
					<SelectComboboxList>
						<SelectContent
							filteredData={filteredData}
							isError={countriesQuery.isError}
							isPending={countriesQuery.isPending}
						/>
					</SelectComboboxList>
				</SelectComboboxPopover>
			</SelectProvider>
		</ComboboxProvider>
	)
}
